import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Database, HardDrive, Trash2, RefreshCw, AlertTriangle, CheckCircle, Download, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDatabase = () => {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [compacting, setCompacting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('admin_token');

  const fetchDiagnostics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/db-diagnostics`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setDiagnostics(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
        return;
      }
      toast.error("Error carregant diagnòstic");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }
    fetchDiagnostics();
  }, [navigate, fetchDiagnostics]);

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      const response = await axios.post(`${API}/admin/cleanup-base64`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success(response.data.message);
      await fetchDiagnostics();
    } catch (error) {
      toast.error("Error durant la neteja");
    } finally {
      setCleaning(false);
    }
  };

  const handleCompact = async () => {
    setCompacting(true);
    try {
      const response = await axios.post(`${API}/admin/compact-db`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const data = response.data;
      if (data.diagnostics_disabled) {
        toast.success("Optimització completada. Diagnòstics desactivats per estalviar espai.");
      } else {
        toast.success("Base de dades compactada correctament");
      }
      await fetchDiagnostics();
    } catch (error) {
      toast.error("Error compactant la base de dades");
    } finally {
      setCompacting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/export-data`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `estelregals_backup_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup descarregat correctament");
    } catch (error) {
      toast.error("Error exportant les dades");
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await axios.post(`${API}/admin/import-data`, data, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }
      });
      toast.success("Dades importades correctament");
      await fetchDiagnostics();
    } catch (error) {
      toast.error("Error important les dades");
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const formatSize = (mb) => {
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${(mb * 1024).toFixed(0)} KB`;
  };

  const collectionLabel = (name) => {
    const labels = {
      products: 'Productes',
      orders: 'Comandes',
      categories: 'Categories',
      admin_users: 'Administradors',
      hero_settings: 'Pàgina principal',
      payment_transactions: 'Pagaments',
      site_settings: 'Configuració',
      reset_codes: 'Codis de reset',
      system_flags: 'Flags del sistema'
    };
    return labels[name] || name;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const hasBase64 = diagnostics?.base64_found?.length > 0;
  const totalStorage = diagnostics?.collections?.reduce((sum, c) => sum + c.storage_size_mb, 0) || 0;
  const maxStorage = Math.max(...(diagnostics?.collections?.map(c => c.storage_size_mb) || [0.01]), 0.01);

  return (
    <div data-testid="admin-database" className="min-h-screen bg-muted/30 px-6 py-12">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-6">
            <ArrowLeft className="h-4 w-4" />
            Tornar al panell
          </Link>
          <div className="flex items-center justify-between">
            <h1 className="font-heading text-4xl font-medium text-foreground">
              Base de Dades
            </h1>
            <Button
              data-testid="refresh-diagnostics-btn"
              variant="outline"
              size="sm"
              onClick={() => { setLoading(true); fetchDiagnostics(); }}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Actualitzar
            </Button>
          </div>
          <p className="text-muted-foreground mt-2">
            Estat de la base de dades i eines de manteniment.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          {/* Summary Card */}
          <div data-testid="db-summary-card" className="bg-card rounded-2xl p-6 shadow-sm border">
            <div className="flex items-center gap-3 mb-4">
              <HardDrive className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-semibold">Resum</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-muted/50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-foreground" data-testid="total-data-size">
                  {formatSize(diagnostics?.total_data_size_mb || 0)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">Dades</p>
              </div>
              <div className="bg-muted/50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-foreground" data-testid="total-storage-size">
                  {formatSize(totalStorage)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">Emmagatzematge</p>
              </div>
            </div>
          </div>

          {/* Base64 Alert */}
          {hasBase64 && (
            <div data-testid="base64-alert" className="bg-orange-50 border-2 border-orange-300 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
                <h2 className="text-lg font-semibold text-orange-800">
                  Imatges base64 detectades
                </h2>
              </div>
              <p className="text-orange-700 mb-4">
                Hi ha {diagnostics.base64_found.length} element(s) amb imatges guardades directament
                a la base de dades. Neteja-les per alliberar espai.
              </p>
              <ul className="text-sm text-orange-600 space-y-1 mb-4">
                {diagnostics.base64_found.map((item, i) => (
                  <li key={i}>
                    {item.name} ({formatSize(item.image_url_length / (1024 * 1024))})
                  </li>
                ))}
              </ul>
              <Button
                data-testid="cleanup-base64-btn"
                onClick={handleCleanup}
                disabled={cleaning}
                className="bg-orange-600 hover:bg-orange-700 gap-2"
              >
                {cleaning ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {cleaning ? 'Netejant...' : 'Netejar imatges base64'}
              </Button>
            </div>
          )}

          {/* No base64 - All clean */}
          {!hasBase64 && (
            <div data-testid="db-clean-status" className="bg-green-50 border-2 border-green-300 rounded-2xl p-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <h2 className="text-lg font-semibold text-green-800">
                    Base de dades neta
                  </h2>
                  <p className="text-green-700 text-sm">
                    No hi ha imatges base64 residuals. Totes les imatges estan al CDN.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Collections */}
          <div className="bg-card rounded-2xl p-6 shadow-sm border">
            <div className="flex items-center gap-3 mb-5">
              <Database className="h-6 w-6 text-primary" />
              <h2 className="text-xl font-semibold">Col·leccions</h2>
            </div>
            <div className="space-y-3">
              {diagnostics?.collections
                ?.sort((a, b) => b.storage_size_mb - a.storage_size_mb)
                .map((coll) => (
                  <div key={coll.name} data-testid={`collection-${coll.name}`} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">
                        {collectionLabel(coll.name)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {coll.documents} docs · {formatSize(coll.storage_size_mb)}
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2.5">
                      <div
                        className="bg-primary rounded-full h-2.5 transition-all duration-500"
                        style={{ width: `${Math.max((coll.storage_size_mb / maxStorage) * 100, 2)}%` }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Compact Button */}
          <div className="bg-card rounded-2xl p-6 shadow-sm border">
            <h3 className="font-semibold text-lg mb-2">Optimitzar base de dades</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Compacta les col·leccions i desactiva la recol·lecció de diagnòstics
              interns de MongoDB (que poden ocupar centenars de MB).
            </p>
            <Button
              data-testid="compact-db-btn"
              onClick={handleCompact}
              disabled={compacting}
              variant="outline"
              className="gap-2"
            >
              {compacting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
              ) : (
                <HardDrive className="h-4 w-4" />
              )}
              {compacting ? 'Optimitzant...' : 'Optimitzar base de dades'}
            </Button>
          </div>

          {/* Export/Import */}
          <div className="bg-card rounded-2xl p-6 shadow-sm border">
            <h3 className="font-semibold text-lg mb-2">Backup i restauració</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Per reduir l'espai del volum a Railway, pots exportar les dades, 
              eliminar el volum a Railway, i tornar a importar.
            </p>
            <div className="flex gap-3 flex-wrap">
              <Button
                data-testid="export-data-btn"
                onClick={handleExport}
                disabled={exporting}
                variant="outline"
                className="gap-2"
              >
                {exporting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {exporting ? 'Exportant...' : 'Exportar dades (JSON)'}
              </Button>
              <label>
                <Button
                  data-testid="import-data-btn"
                  variant="outline"
                  className="gap-2 cursor-pointer"
                  disabled={importing}
                  asChild
                >
                  <span>
                    {importing ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    {importing ? 'Important...' : 'Importar dades'}
                  </span>
                </Button>
                <input type="file" accept=".json" onChange={handleImport} className="hidden" />
              </label>
            </div>
          </div>

          {/* Info Card - How to reduce Railway volume */}
          <div className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
            <h3 className="font-semibold text-amber-800 mb-3">Com reduir l'espai a Railway</h3>
            <ol className="text-sm text-amber-700 space-y-2 list-decimal list-inside">
              <li><strong>Exportar</strong> — Prem "Exportar dades" i guarda el fitxer JSON</li>
              <li><strong>A Railway</strong> — Ves a MongoDB → Settings → elimina el volum</li>
              <li><strong>Recrear</strong> — Crea un nou volum i reinicia MongoDB</li>
              <li><strong>Importar</strong> — Prem "Importar dades" i selecciona el fitxer JSON</li>
            </ol>
            <p className="text-xs text-amber-600 mt-3">
              Això reduirà l'espai de ~1GB a ~300MB (el mínim de MongoDB).
            </p>
          </div>

          {/* Why Railway shows more space */}
          <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">Per què Railway mostra ~1GB?</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>Les teves dades reals ocupen menys d'1 MB gràcies al CDN</li>
              <li>MongoDB necessita ~300MB per als seus fitxers de journal (obligatoris)</li>
              <li>Les dades de diagnòstic acumulades poden ocupar centenars de MB</li>
              <li>Recrear el volum és l'única forma de recuperar tot l'espai</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminDatabase;
